import { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  CameraAlt as CameraIcon,
  FlipCameraIos as FlipCameraIcon,
  Replay as RetryIcon,
  Videocam as VideoIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Warning as WarningIcon,
  PhotoLibrary,
  StopRounded,
  PlayArrowRounded,
} from "@mui/icons-material";
import {
  checkAndResizeImage,
  readFileAsDataURL,
  detectBlur,
} from "@/utils/helper";
import { BlurResult } from "@/types/common";

const styles = {
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "0px",
      maxWidth: "100%",
      width: "100%",
      height: "100%",
      margin: "0",
      maxHeight: "100%",
      overflow: "hidden",
      backgroundColor: "#000",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "2.5% 4%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
  },
  title: {
    fontSize: { xs: "4.37vw", sm: 18 },
    fontWeight: 700,
    color: "#fff",
    fontFamily: "Roboto, sans-serif",
  },
  content: {
    padding: 0,
    backgroundColor: "#000",
    textAlign: "center" as const,
    height: "100%",
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  },
  webcamContainer: {
    width: "100%",
    height: "100%",
    position: "relative" as const,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  webcam: {
    width: "100%",
    height: "100%",
    objectFit: "cover" as const,
  },
  captureButton: {
    backgroundColor: "#6f2fa8",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "16px",
    padding: "16px 32px",
    borderRadius: "50%",
    width: "70px",
    height: "70px",
    minWidth: "auto",
    position: "absolute" as const,
    bottom: "30px",
    left: "50%",
    transform: "translateX(-50%)",
    "&:hover": {
      backgroundColor: "#5a2380",
    },
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  },
  flipButton: {
    position: "absolute" as const,
    bottom: "40px",
    right: "20px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    width: "50px",
    height: "50px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  pauseResumeButton: {
    position: "absolute" as const,
    bottom: "40px",
    left: "20px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    width: "50px",
    height: "50px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  doneButton: {
    position: "absolute" as const,
    bottom: "40px",
    right: "80px",
    backgroundColor: "#4caf50",
    color: "#fff",
    width: "50px",
    height: "50px",
    "&:hover": {
      backgroundColor: "#388e3c",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      color: "#fff",
    },
  },
  retakeButton: {
    position: "absolute" as const,
    top: "40px",
    right: "80px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "16px",
    padding: "12px 24px",
    borderRadius: "8px",
    marginRight: "12px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  acceptButton: {
    backgroundColor: "#6f2fa8",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: { xs: "3.88vw", sm: 16 },
    py: "2%",
    px: "5%",
    mb: "4%",
    borderRadius: "6px",
    "&:hover": {
      backgroundColor: "#5a2380",
    },
    "&.Mui-disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      color: "#ccc",
    },
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "center",
    position: "absolute" as const,
    bottom: "30px",
    left: 0,
    right: 0,
    zIndex: 10,
  },
  previewImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    pointerEvents: "none",
  } as React.CSSProperties,
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#fff",
    flexDirection: "column" as const,
    gap: "16px",
  },
  errorContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    color: "#fff",
    flexDirection: "column" as const,
    padding: "20px",
    textAlign: "center" as const,
    gap: "16px",
  },
  retryButton: {
    marginTop: "3%",
    backgroundColor: "#6f2fa8",
    borderRadius: "6px",
    color: "#fff",
    fontSize: { xs: "2.91vw", sm: 14 },
    py: "2%",
    px: "5%",
    "&:hover": {
      backgroundColor: "#5a2380",
    },
  },
  photoCounter: {
    position: "absolute" as const,
    top: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "16px",
    fontWeight: 600,
  },
  thumbnailContainer: {
    position: "absolute" as const,
    bottom: "30px",
    left: "20px",
    width: "60px",
    height: "60px",
    zIndex: -1,
  },
  thumbnail: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    border: "2px solid #fff",
    objectFit: "cover" as const,
  },
  blurryThumbnail: {
    width: "60px",
    height: "60px",
    borderRadius: "8px",
    border: "2px solid #ff3b30",
    objectFit: "cover" as const,
  },
  galleryNote: {
    position: "absolute" as const,
    bottom: "30vw",
    left: "20%",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "#fff",
    padding: "1vw 2.5vw",
    borderRadius: "3vw",
    fontSize: { xs: "2.91vw", sm: 12 },
    whiteSpace: "nowrap" as const,
    zIndex: 10,
  },
  videoButton: {
    position: "absolute" as const,
    bottom: "40px",
    left: "20px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    width: "50px",
    height: "50px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  recordingIndicator: {
    position: "absolute" as const,
    top: "70px",
    right: "20px",
    backgroundColor: "#ff3b30",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pausedIndicator: {
    position: "absolute" as const,
    top: "70px",
    right: "20px",
    backgroundColor: "#ff9500",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: "4px",
    fontSize: { xs: "2.91vw", sm: 14 },
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  captionContainer: {
    position: "absolute" as const,
    top: "100px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: { xs: "3.4vw", md: 16 },
    fontWeight: 600,
    zIndex: 10,
  },
  progressIndicator: {
    position: "absolute" as const,
    top: "20px",
    left: "20px",
    display: "flex",
    gap: "4px",
  },
  progressDot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: "rgba(255, 255, 255, 0.3)",
  },
  activeProgressDot: {
    backgroundColor: "#6f2fa8",
  },
  completedProgressDot: {
    backgroundColor: "#4caf50",
  },
  blurryProgressDot: {
    backgroundColor: "#ff3b30",
  },
  modeToggle: {
    position: "absolute" as const,
    top: "20px",
    right: "60px",
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    color: "#fff",
    width: "50px",
    height: "50px",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
    },
  },
  videoPlaybackControls: {
    position: "absolute" as const,
    top: "60px",
    right: "20px",
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    zIndex: 20,
  },
  previewCaption: {
    position: "absolute" as const,
    top: "8%",
    left: "50%",
    transform: "translateX(-50%)",
    color: "#fff",
    fontSize: { xs: "4.85vw", sm: 20 },
    fontWeight: 600,
    backgroundColor: "rgba(0,0,0,0.6)",
    px: 3,
    py: 1,
    borderRadius: "25px",
    zIndex: 10,
  },
  blurWarning: {
    position: "absolute" as const,
    top: "140px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(255, 59, 48, 0.9)",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    zIndex: 15,
  },
  blurryPhotoOverlay: {
    position: "absolute" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(255, 58, 48, 0.03)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column" as const,
    gap: "8px",
  },
  checkingBlur: {
    position: "absolute" as const,
    top: "140px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "rgba(111, 47, 168, 0.9)",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: "20px",
    fontSize: "14px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    zIndex: 15,
  },
};

interface PhotoCaptureModalProps {
  open: boolean;
  onClose: () => void;
  onPhotosTaken: (photos?: string[], video?: string | Blob) => void;
  maxPhotos?: number;
  allowVideo?: boolean;
  photoCaptions?: string[];
  mode?: "photo" | "video";
  blurThreshold?: number;
}

export default function PhotoCaptureModal({
  open,
  onClose,
  onPhotosTaken,
  maxPhotos = 3,
  allowVideo = true,
  photoCaptions = ["Front View", "Side View", "Wide View"],
  mode = "photo",
  blurThreshold = 150,
}: PhotoCaptureModalProps) {
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const webcamRef = useRef<Webcam>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [photoBlurResults, setPhotoBlurResults] = useState<BlurResult[]>([]);
  const [checkingBlur, setCheckingBlur] = useState(false);
  const [facingMode, setFacingMode] = useState<"user" | "environment">(
    "environment"
  );
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState<string | null>(null);
  const [currentMode, setCurrentMode] = useState<"photo" | "video">(mode);
  const [errorMessage, setErrorMessage] = useState("");
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [recordedVideoBlob, setRecordedVideoBlob] = useState<Blob | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [showPlayback, setShowPlayback] = useState(false);
  const [retakeIndex, setRetakeIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setCapturedPhotos([]);
      setPhotoBlurResults([]);
      setCheckingBlur(false);
      setHasError(false);
      setIsRecording(false);
      setIsPaused(false);
      setRecordedVideo(null);
      setCurrentMode(mode);
      setErrorMessage("");
      setIsLoading(true);
      setRecordingTime(0);
      setShowPlayback(false);
      setRetakeIndex(null);
      checkCameraAvailability();
    }
  }, [open, mode]);

  useEffect(() => {
    const currentWebcam = webcamRef.current;
    const currentMediaRecorder = mediaRecorderRef.current;
    const currentRecordingTimer = recordingTimerRef.current;
    const currentFileInput = fileInputRef.current;

    return () => {
      if (currentWebcam && currentWebcam.stream) {
        currentWebcam.stream.getTracks().forEach((track) => track.stop());
      }
      if (
        currentMediaRecorder &&
        currentMediaRecorder.state === "recording"
      ) {
        currentMediaRecorder.stop();
      }
      if (currentRecordingTimer) {
        clearInterval(currentRecordingTimer);
      }
      if (currentFileInput) {
        currentFileInput.remove();
      }
    };
  }, []);

  const checkCameraAvailability = async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      setCameraAvailable(videoDevices.length > 0);

      if (videoDevices.length === 0) {
        setHasError(true);
        setErrorMessage(
          "No camera found. Please connect a camera and try again."
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error checking camera availability:", error);
      setHasError(true);
      setErrorMessage(
        "Unable to access camera devices. Please check your permissions."
      );
      setIsLoading(false);
    }
  };

  const capturePhoto = async () => {
    if (!webcamRef.current) return;
    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) return;

    setCheckingBlur(true);

    try {
      const blurResult = await detectBlur(imageSrc, blurThreshold);

      if (retakeIndex !== null) {
        const updatedPhotos = [...capturedPhotos];
        const updatedBlurResults = [...photoBlurResults];
        updatedPhotos[retakeIndex] = imageSrc;
        updatedBlurResults[retakeIndex] = blurResult;
        setCapturedPhotos(updatedPhotos);
        setPhotoBlurResults(updatedBlurResults);
        setRetakeIndex(null);
      } else if (capturedPhotos.length < maxPhotos) {
        setCapturedPhotos([...capturedPhotos, imageSrc]);
        setPhotoBlurResults([...photoBlurResults, blurResult]);
      }
    } catch (error) {
      console.error("Error detecting blur:", error);
      // If blur detection fails, allow the photo anyway
      if (retakeIndex !== null) {
        const updated = [...capturedPhotos];
        updated[retakeIndex] = imageSrc;
        setCapturedPhotos(updated);
        setRetakeIndex(null);
      } else if (capturedPhotos.length < maxPhotos) {
        setCapturedPhotos([...capturedPhotos, imageSrc]);
        setPhotoBlurResults([
          ...photoBlurResults,
          { isBlurry: false, blurScore: blurThreshold + 1 },
        ]);
      }
    } finally {
      setCheckingBlur(false);
    }
  };

  const acceptMedia = () => {
    if (currentMode === "photo" && capturedPhotos.length > 0) {
      onPhotosTaken(capturedPhotos);
    } else if (currentMode === "video" && recordedVideoBlob) {
      onPhotosTaken([], recordedVideoBlob);
    }
    onClose();
  };

  const toggleCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleError = (error: string | DOMException) => {
    setIsLoading(false);
    setHasError(true);
    console.error("Camera Error:", error);

    let message =
      "Unable to access the camera. Please check your camera connection and permissions.";
    if (typeof error === "string") {
      message = error;
    } else if (error.name === "NotAllowedError") {
      message =
        "Permission denied. Please allow camera access in your browser settings.";
    } else if (
      error.name === "NotFoundError" ||
      error.name === "OverconstrainedError"
    ) {
      message = "No suitable camera found. Switching camera...";
      setTimeout(() => {
        setFacingMode(facingMode === "user" ? "environment" : "user");
        retryCamera();
      }, 1000);
      return;
    } else if (error.name === "NotReadableError") {
      message =
        "Camera is in use by another application. Please close it and try again.";
    }

    setErrorMessage(message);
  };

  const retryCamera = () => {
    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");
  };

  // Check if any photos are blurry
  const hasBlurryPhotos = () => {
    return (
      currentMode === "photo" &&
      photoBlurResults.some((result) => result.isBlurry)
    );
  };

  // Get current blur status for display
  const getCurrentBlurStatus = () => {
    if (currentMode !== "photo") return null;
    if (retakeIndex !== null) {
      return null; // No status while retaking
    }
    return null;
  };

  const startRecording = async () => {
    if (webcamRef.current && webcamRef.current.stream && !isRecording) {
      const stream = webcamRef.current.stream;

      try {
        const mimeType = getSupportedMimeType();
        const chunks: Blob[] = [];

        // Clear any previous error state
        setHasError(false);
        setErrorMessage("");

        mediaRecorderRef.current = new MediaRecorder(stream, {
          mimeType,
          videoBitsPerSecond: 2500000,
        });

        mediaRecorderRef.current.ondataavailable = (event) => {
          if (event.data && event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        mediaRecorderRef.current.onstop = () => {
          if (chunks.length > 0) {
            const blob = new Blob(chunks, { type: mimeType });
            const videoURL = URL.createObjectURL(blob);

            setRecordedVideo(videoURL);
            setRecordedVideoBlob(blob);
            setShowPlayback(true);
            setHasError(false); // Ensure error is cleared on successful recording

            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.load();
              }
            }, 0);
          }
          setIsRecording(false);
          setIsPaused(false);
          setRecordingTime(0);
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
          }
        };

        // Fix: Handle MediaRecorder errors more gracefully
        mediaRecorderRef.current.onerror = (event) => {
          console.error("MediaRecorder error:", event);
          // Don't show error if we're already stopping or have chunks
          if (chunks.length > 0 && (isRecording || isPaused)) {
            return;
          }
          setErrorMessage("Recording failed. Please try again.");
          setHasError(true);
          setIsRecording(false);
          setIsPaused(false);
          setRecordingTime(0);
          if (recordingTimerRef.current) {
            clearInterval(recordingTimerRef.current);
          }
        };

        mediaRecorderRef.current.start(1000);
        setIsRecording(true);
        setIsPaused(false);

        setRecordingTime(0);
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
        }
        recordingTimerRef.current = setInterval(() => {
          setRecordingTime((previousSeconds) => {
            const nextSeconds = previousSeconds + 1;
            if (nextSeconds > 30) {
              if (
                mediaRecorderRef.current &&
                mediaRecorderRef.current.state === "recording"
              ) {
                mediaRecorderRef.current.stop();
              }
              if (recordingTimerRef.current) {
                clearInterval(recordingTimerRef.current);
              }
              return 30;
            }
            return nextSeconds;
          });
        }, 1000);
      } catch (err) {
        console.error("Error starting recording:", err);
        setErrorMessage(
          ` Failed to start recording: ${
            err instanceof Error ? err.message : "Unknown error"
          }`
        );
        setHasError(true);
      }
    }
  };

  const pauseRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      setIsPaused(false);

      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime((previousSeconds) => {
          const nextSeconds = previousSeconds + 1;
          if (nextSeconds > 30) {
            if (
              mediaRecorderRef.current &&
              mediaRecorderRef.current.state === "recording"
            ) {
              mediaRecorderRef.current.stop();
            }
            if (recordingTimerRef.current) {
              clearInterval(recordingTimerRef.current);
            }
            return 30;
          }
          return nextSeconds;
        });
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (
      mediaRecorderRef.current &&
      (isRecording || isPaused) &&
      recordingTime > 1
    ) {
      mediaRecorderRef.current.stop();
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const retakeVideo = () => {
    if (recordedVideo) {
      try {
        URL.revokeObjectURL(recordedVideo);
      } catch (e) {
        // ignore revoke errors
      }
    }
    setRecordedVideo(null);
    setShowPlayback(false);
    setRecordingTime(0);
    setHasError(false);
    setIsPaused(false);
  };

  // const getSupportedMimeType = () => {
  //   const types = [
  //     'video/webm;codecs=vp9,opus',
  //     'video/webm;codecs=vp8,opus',
  //     'video/webm;codecs=h264,opus',
  //     'video/mp4;codecs=h264,aac',
  //     'video/webm',
  //     'video/mp4'
  //   ];

  //   for (const type of types) {
  //     if (MediaRecorder.isTypeSupported(type)) {
  //       return type;
  //     }
  //   }

  //   return '';
  // };

  const getSupportedMimeType = () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

    if (isIOS) {
      // iOS specific formats
      return "video/mp4"; // iOS only supports mp4
    } else {
      // Android/other browsers
      const types = [
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=h264,opus",
        "video/webm",
        "video/mp4",
      ];

      for (const type of types) {
        if (MediaRecorder.isTypeSupported(type)) return type;
      }
      return "video/mp4";
    }
  };

  const getCurrentCaption = () => {
    if (currentMode === "video") {
      if (showPlayback) return "Play your recorded video";
      if (isPaused) return "Recording paused - tap resume to continue";
      if (isRecording) return "Recording...";
      return "Start recording";
    }

    if (retakeIndex !== null)
      return photoCaptions[retakeIndex] || `Photo ${retakeIndex + 1}`;
    if (capturedPhotos.length >= maxPhotos) return "All photos captured";
    return (
      photoCaptions[capturedPhotos.length] ||
      `Photo ${capturedPhotos.length + 1}`
    );
  };

  const toggleMode = () => {
    if (isRecording) {
      stopRecording();
    }
    setCurrentMode((prev) => (prev === "photo" ? "video" : "photo"));
    setRecordedVideo(null);
    setShowPlayback(false);
    setRecordingTime(0);
    setIsPaused(false);
  };

  const isDoneDisabled = () => {
    if (currentMode === "photo") {
      return capturedPhotos.length < maxPhotos || hasBlurryPhotos();
    } else {
      return !recordedVideo;
    }
  };

  const getVideoConstraints = () => ({
    facingMode,
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 24, max: 30 },
  });

  const closePreview = () => {
    setPreviewIndex(null);
  };

  // Add CSS animation for blinking effect
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes blink {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
    `;
    document.head.append(style);
    return () => style.remove();
  }, []);

  const handleGalleryUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith("image/")) {
      setErrorMessage("Please select an image file");
      setHasError(true);
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setErrorMessage("Image size should be less than 10MB");
      setHasError(true);
      return;
    }

    try {
      // const fromGalleryCamera = await isFromGalleryCamera(file);
      const imageDataUrl = await readFileAsDataURL(file);

      // Check if we have room for more photos
      if (retakeIndex !== null) {
        // Replace existing photo
        await processSelectedImage(
          imageDataUrl,
          retakeIndex,
          // fromGalleryCamera
        );
        setRetakeIndex(null);
      } else if (capturedPhotos.length < maxPhotos) {
        // Add new photo
        await processSelectedImage(
          imageDataUrl,
          capturedPhotos.length,
          // fromGalleryCamera
        );
      }
    } catch (error) {
      console.error("Error processing selected image:", error);
      setErrorMessage("Failed to process the selected image");
      setHasError(true);
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const processSelectedImage = async (
    imageDataUrl: string,
    index: number,
    // fromGalleryCamera: boolean
  ) => {
    setCheckingBlur(true);

    try {
      const resizedImageDataUrl = await checkAndResizeImage(imageDataUrl);

      const blurResult = await detectBlur(resizedImageDataUrl, blurThreshold);

      // if (fromGalleryCamera) {
      if (blurResult.blurScore >= 20 && blurResult.blurScore < 200) {
        blurResult.isBlurry = false;
      }
      // }

      if (index < capturedPhotos.length) {
        // Replace existing photo (retake scenario)
        const updatedPhotos = [...capturedPhotos];
        const updatedBlurResults = [...photoBlurResults];
        updatedPhotos[index] = resizedImageDataUrl;
        updatedBlurResults[index] = blurResult;
        setCapturedPhotos(updatedPhotos);
        setPhotoBlurResults(updatedBlurResults);
      } else {
        // Add new photo
        setCapturedPhotos([...capturedPhotos, resizedImageDataUrl]);
        setPhotoBlurResults([...photoBlurResults, blurResult]);
      }
    } catch (error) {
      console.error("Error processing image:", error);
      // If blur detection fails, allow the photo anyway
      if (index < capturedPhotos.length) {
        const updated = [...capturedPhotos];
        updated[index] = imageDataUrl;
        setCapturedPhotos(updated);
      } else {
        setCapturedPhotos([...capturedPhotos, imageDataUrl]);
        setPhotoBlurResults([
          ...photoBlurResults,
          { isBlurry: false, blurScore: blurThreshold + 1 },
        ]);
      }
    } finally {
      setCheckingBlur(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog} fullScreen>
      <Box sx={styles.header}>
        <Typography sx={styles.title}>
          {currentMode === "photo" ? "Take Photos" : "Record Video"}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon fontSize="medium" />
        </IconButton>
      </Box>

      <DialogContent sx={styles.content}>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg,.jpeg,.png,.doc"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />
        {!isLoading &&
          currentMode === "photo" &&
          (capturedPhotos.length != maxPhotos || retakeIndex !== null) && (
            <IconButton
              sx={{
                position: "absolute" as const,
                bottom: "40px",
                left: "5%",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#fff",
                width: "50px",
                height: "50px",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                },
              }}
              onClick={handleGalleryUpload}
            >
              <PhotoLibrary />
            </IconButton>
          )}

        {!isLoading &&
          currentMode === "photo" &&
          (capturedPhotos.length != maxPhotos || retakeIndex !== null) && (
            <Typography sx={styles.galleryNote}>
              Only images under 10 MB can be uploaded
            </Typography>
          )}

        {isLoading && !hasError && (
          <Box sx={styles.loadingContainer}>
            <CircularProgress sx={{ color: "#6f2fa8" }} />
            <Typography>Loading camera...</Typography>
          </Box>
        )}

        {hasError && !capturedPhotos?.length && (
          <Box sx={styles.errorContainer}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, fontSize: { xs: "4.37vw", sm: 18 } }}
            >
              Camera Error
            </Typography>
            <Typography sx={{ fontSize: { xs: "3.4vw", sm: 16 } }}>
              {errorMessage ||
                "Unable to access the camera. Please check your permissions and try again."}
            </Typography>
            {cameraAvailable && (
              <Button
                sx={styles.retryButton}
                onClick={retryCamera}
                startIcon={<RetryIcon />}
              >
                Retry
              </Button>
            )}
          </Box>
        )}

        {previewIndex !== null && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0,0,0,0.95)",
              display: "flex",
              flexDirection: "column",
              zIndex: 20,
            }}
          >
            {/* Close button */}
            <IconButton
              onClick={closePreview}
              sx={{
                position: "absolute",
                top: 20,
                left: 20,
                color: "#fff",
                zIndex: 30,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Retake button pinned top-right */}
            <Button
              sx={{
                position: "absolute",
                top: "5%",
                right: 20,
                backgroundColor: "#ff3b30",
                color: "#fff",
                borderRadius: "6px",
                fontSize: { xs: "2.91vw", sm: 12 },
                "&:hover": { backgroundColor: "#d32f2f" },
                zIndex: 30,
              }}
              onClick={() => {
                if (previewIndex !== null) {
                  setRetakeIndex(previewIndex);
                  closePreview();
                }
              }}
            >
              Retake
            </Button>

            {/* Carousel */}
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                width: "100%",
                height: "100%",
                scrollSnapType: "x mandatory",
              }}
            >
              {capturedPhotos.map((photo, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "0 0 100%",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    scrollSnapAlign: "center",
                    flexDirection: "column",
                  }}
                >
                  <img
                    src={photo}
                    alt={`Captured ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "80%",
                      objectFit: "contain",
                    }}
                  />
                  <Typography sx={styles.previewCaption}>
                    {photoCaptions[index] || `Photo ${index + 1}`}
                  </Typography>
                  {/* Show blur warning overlay */}
                  {photoBlurResults[index]?.isBlurry && (
                    <Box sx={styles.blurryPhotoOverlay}>
                      <WarningIcon sx={{ fontSize: 48, color: "#fff" }} />
                      <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                        This photo is not clear
                      </Typography>
                      <Typography sx={{ color: "#fff", fontSize: "14px" }}>
                        Please retake for better quality
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {showPlayback && recordedVideo ? (
          <>
            <Box sx={styles.webcamContainer}>
              <video
                ref={videoRef}
                src={recordedVideo}
                controls={false}
                autoPlay
                playsInline
                muted={false} // Remove muted for iOS
                style={styles.previewImage}
                onEnded={() => {
                  if (videoRef.current) {
                    videoRef.current.currentTime = 0;
                  }
                }}
              />
            </Box>
            <Button
              sx={{
                position: "absolute",
                top: 70,
                right: 20,
                backgroundColor: "#ff3b30",
                color: "#fff",
                "&:hover": { backgroundColor: "#d32f2f" },
                zIndex: 30,
              }}
              onClick={retakeVideo}
            >
              Retake
            </Button>
            <Box
              sx={{
                position: "absolute",
                bottom: "30px",
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                gap: "12px",
              }}
            >
              <Button sx={styles.acceptButton} onClick={acceptMedia}>
                Use Video
              </Button>
            </Box>
          </>
        ) : currentMode === "photo" &&
          capturedPhotos.length === maxPhotos &&
          retakeIndex === null ? (
          <>
            <Box
              sx={{
                display: "flex",
                overflowX: "auto",
                width: "100%",
                height: "100%",
                scrollSnapType: "x mandatory",
              }}
            >
              {capturedPhotos.map((photo, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: "0 0 100%",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    scrollSnapAlign: "center",
                  }}
                >
                  <img
                    src={photo}
                    alt={`Captured ${index + 1}`}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <Typography sx={styles.previewCaption}>
                    {photoCaptions[index] || `Photo ${index + 1}`}
                  </Typography>
                  <Button
                    sx={{
                      position: "absolute",
                      top: 70,
                      right: 20,
                      backgroundColor: "#ff3b30",
                      color: "#fff",
                      "&:hover": { backgroundColor: "#d32f2f" },
                      zIndex: 30,
                    }}
                    onClick={() => {
                      setRetakeIndex(index);
                      setPreviewIndex(null);
                    }}
                  >
                    Retake
                  </Button>
                  {/* Show blur warning overlay */}
                  {photoBlurResults[index]?.isBlurry && (
                    <Box sx={styles.blurryPhotoOverlay}>
                      <WarningIcon sx={{ fontSize: 48, color: "#fff" }} />
                      <Typography sx={{ color: "#fff", fontWeight: 600 }}>
                        This photo is not clear
                      </Typography>
                      <Typography sx={{ color: "#fff", fontSize: "14px" }}>
                        Please retake for better quality
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
            {photoBlurResults.some((r) => r?.isBlurry) ? (
              <Button sx={styles.acceptButton} onClick={acceptMedia}>
                Submit Anyway
              </Button>
            ) : (
              <Button
                sx={styles.acceptButton}
                onClick={acceptMedia}
                disabled={isDoneDisabled()}
              >
                Submit
              </Button>
            )}
          </>
        ) : (
          !hasError && (
            <>
              <Box sx={styles.webcamContainer}>
                {currentMode === "video" && showPlayback && recordedVideo ? (
                  <video
                    ref={videoRef}
                    src={recordedVideo}
                    controls={false}
                    autoPlay
                    playsInline
                    style={styles.previewImage}
                  />
                ) : (
                  <Webcam
                    ref={webcamRef}
                    muted={true}
                    screenshotFormat="image/jpeg"
                    style={styles.webcam}
                    videoConstraints={getVideoConstraints()}
                    audio={false}
                    mirrored={facingMode === "user"}
                    onUserMedia={handleLoad}
                    onUserMediaError={handleError}
                    screenshotQuality={0.8}
                    forceScreenshotSourceSize={false}
                    playsInline={true}
                  />
                )}

                {!isLoading && (
                  <>
                    {currentMode === "photo" && (
                      <Box sx={styles.progressIndicator}>
                        {Array.from({ length: maxPhotos }).map((_, index) => (
                          <Box
                            key={index}
                            sx={{
                              ...styles.progressDot,
                              ...(index < capturedPhotos.length
                                ? photoBlurResults[index]?.isBlurry
                                  ? styles.blurryProgressDot
                                  : styles.completedProgressDot
                                : {}),
                              ...((
                                retakeIndex !== null
                                  ? index === retakeIndex
                                  : index === capturedPhotos.length
                              )
                                ? styles.activeProgressDot
                                : {}),
                            }}
                          />
                        ))}
                      </Box>
                    )}

                    <Typography sx={styles.captionContainer}>
                      {getCurrentCaption()}
                    </Typography>

                    {currentMode === "photo" && (
                      <Typography sx={styles.photoCounter}>
                        {capturedPhotos.length} / {maxPhotos}
                      </Typography>
                    )}

                    {/* Blur checking indicator */}
                    {checkingBlur && (
                      <Box sx={styles.checkingBlur}>
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                        Checking image quality...
                      </Box>
                    )}

                    {/* Blur warning */}
                    {getCurrentBlurStatus() && (
                      <Box sx={styles.blurWarning}>
                        <WarningIcon sx={{ fontSize: 16 }} />
                        {getCurrentBlurStatus()}
                      </Box>
                    )}

                    {currentMode === "video" && isRecording && !isPaused && (
                      <Box sx={styles.recordingIndicator}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "6px",
                            backgroundColor: "#fff",
                            animation: "blink 1s infinite",
                            fontSize: { xs: "2.91vw", sm: 14 },
                          }}
                        />
                        {30 - recordingTime}
                      </Box>
                    )}

                    {currentMode === "video" && isPaused && (
                      <Box sx={styles.pausedIndicator}>
                        <PauseIcon sx={{ fontSize: 16 }} />
                        Paused: {recordingTime}s / 30s
                      </Box>
                    )}

                    {/* Pause/Resume button for video recording */}
                    {currentMode === "video" &&
                      isRecording &&
                      !showPlayback && (
                        <IconButton
                          sx={styles.pauseResumeButton}
                          onClick={isPaused ? resumeRecording : pauseRecording}
                          size="large"
                        >
                          {isPaused ? <PlayIcon /> : <PauseIcon />}
                        </IconButton>
                      )}

                    <IconButton
                      disabled={isRecording}
                      sx={styles.flipButton}
                      onClick={toggleCamera}
                      size="large"
                    >
                      <FlipCameraIcon />
                    </IconButton>

                    {allowVideo && currentMode === "photo" && (
                      <IconButton
                        sx={styles.modeToggle}
                        onClick={toggleMode}
                        size="large"
                      >
                        <VideoIcon />
                      </IconButton>
                    )}
                  </>
                )}
              </Box>

              {!isLoading && currentMode === "photo" && (
                <Button
                  sx={styles.captureButton}
                  onClick={capturePhoto}
                  size="large"
                  disabled={
                    (capturedPhotos.length >= maxPhotos &&
                      retakeIndex === null) ||
                    checkingBlur
                  }
                >
                  <CameraIcon sx={{ fontSize: 30 }} />
                </Button>
              )}

              {!isLoading && currentMode === "video" && !showPlayback && (
                <Button
                  sx={{
                    ...styles.captureButton,
                    backgroundColor:
                      isRecording || isPaused ? "#ff3b30" : "#6f2fa8",
                    "&:hover": {
                      backgroundColor:
                        isRecording || isPaused ? "#d32f2f" : "#5a2380",
                    },
                  }}
                  onClick={
                    isRecording || isPaused ? stopRecording : startRecording
                  }
                  size="large"
                >
                  {isRecording || isPaused ? (
                    <StopRounded sx={{ fontSize: 30 }} />
                  ) : (
                    <PlayArrowRounded sx={{ fontSize: 30 }} />
                  )}
                </Button>
              )}
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
